import React, { useState, useEffect } from 'react'
import Header from "../../components/layouts/Header"
import Footer from "../../components/layouts/Footer"
import { Province, Customer } from '../../lib/Types';
import { useAuthCustomer } from "../../components/hooks/useAuthCustomer";
import Swal from "sweetalert2";
import FormLoading from "../../components/loadding/FormLoading";
import LoadingData from '../../components/loadding/LoadingData';

const Profile = () => {
    const { customer } = useAuthCustomer();
    const [loadingOrder, setLoadingOrder] = useState(false);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [provinceId, setProvinceId] = useState("");
    const [districtId, setDistrictId] = useState("");
    const [wardId, setWardId] = useState("");
    const selectedProvince = provinces.find(p => p.Id === provinceId);
    const districts = selectedProvince?.Districts || [];
    const selectedDistrict = districts.find(d => d.Id === districtId);
    const wards = selectedDistrict?.Wards || [];
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [loadingAddress, setLoadingAddress] = useState(false);
    const [getCustomer, setGetCustomer] = useState<Customer | null>(null);
    const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        name: "",
        email: "",
        tel: "",
        note_address: "",
    });

    const fetchDeliveryAddress = async () => {
        try {
            if (!customer) return;

            setLoadingAddress(true);

            const params = new URLSearchParams({
                customer_id: customer.id.toString(),
            });

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/customer/get-delivery-address?${params.toString()}`
            );

            if (!res.ok) throw new Error("Fetch failed");

            const json = await res.json();
            setGetCustomer(json.data);

        } catch (error) {
            console.error(error);
        } finally {
            setLoadingAddress(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const provinceName =
                provinces.find(p => String(p.Id) === String(provinceId))?.Name || "";

            const districtName =
                districts.find(d => String(d.Id) === String(districtId))?.Name || "";

            const wardName =
                wards.find(w => String(w.Id) === String(wardId))?.Name || "";

            const payload = {
                name: form.name,
                email: form.email,
                tel: form.tel,
                province: provinceName,
                district: districtName,
                ward: wardName,
                note_address: form.note_address,
            };

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/customer/update-profile/${editingAddressId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Update failed");
            }
            setLoading(false);

            await Swal.fire({
                icon: "success",
                title: "Thành công",
                text: "Cập nhật hồ sơ thành công!",
                timer: 1500,
                showConfirmButton: false,
            });

            localStorage.setItem("customer", JSON.stringify(data.data));

            fetchDeliveryAddress();

        } catch (error) {
            console.error("error");
            Swal.fire({
                icon: "error",
                title: "Lỗi",
            });
        } finally {
            setLoading(false);

        }
    };

    useEffect(() => {
        fetchDeliveryAddress();

        fetch("/data/vietnam-address.json")
            .then(res => res.json())
            .then(data => setProvinces(data));
    }, []);

    useEffect(() => {
        if (!customer) return;
        fetchDeliveryAddress();
    }, [customer]);

    useEffect(() => {
        if (!getCustomer) return;

        setEditingAddressId(getCustomer.id)

        setForm({
            name: getCustomer.customer_name ?? "",
            email: getCustomer.email ?? "",
            tel: getCustomer.tel ?? "",
            note_address: getCustomer.note_address ?? "",
        });
    }, [getCustomer]);

    useEffect(() => {
        if (!getCustomer || provinces.length === 0) return;

        const province = provinces.find(p => p.Name === getCustomer.province);
        if (!province) return;

        setProvinceId(province.Id);

        const district = province.Districts.find(
            d => d.Name === getCustomer.district
        );

        if (!district) return;

        setDistrictId(district.Id);

        const ward = district.Wards.find(
            w => w.Name === getCustomer.ward
        );

        if (!ward) return;

        setWardId(ward.Id);
    }, [getCustomer, provinces]);

    return (
        <>
            <Header />

            <div className="text-4xl font-bold text-center pt-24">Hồ sơ</div>

            <div className="max-w-[104rem] w-[96%] m-auto pb-[8rem]">
                {loadingAddress ? (<LoadingData />) : 
                <form onSubmit={handleUpdate} className="max-w-[65rem] mt-12 mx-auto flex flex-col gap-8 px-8 sm:px-0">
                    <div className="flex gap-8">
                        <div className="flex flex-col gap-2 w-full">
                            <label className="text-[1.4rem]">Họ và tên<span className="text-red-500 ml-1">*</span></label>
                            <input
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                className={`border rounded-xl px-6 py-4 text-2xl ${errors.name ? "border border-[#F32637]" : ""}`}
                                placeholder="Nguyễn Văn A"
                            />
                            {errors.name && (
                                <p className="text-[#F32637] text-base">
                                    {errors.name[0]}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-[1.4rem]">Email<span className="text-red-500 ml-1">*</span></label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className={`border rounded-xl px-6 py-4 text-2xl ${errors.email ? "border border-[#F32637]" : ""}`}
                        />
                        {errors.email && (
                            <p className="text-[#F32637] text-base">
                                {errors.email[0]}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-[1.4rem]">Số điện thoại<span className="text-red-500 ml-1">*</span></label>
                        <input
                            type="number"
                            name="tel"
                            value={form.tel}
                            onChange={handleChange}
                            className={`border rounded-xl px-6 py-4 text-2xl ${errors.tel ? "border border-[#F32637]" : ""}`}
                        />
                        {errors.tel && (
                            <p className="text-[#F32637] text-base">
                                {errors.tel[0]}
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Province */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[1.4rem]">
                                Tỉnh / Thành phố<span className="text-red-500 ml-1">*</span>
                            </label>
                            <select
                                name="province_id"
                                value={provinceId}
                                onChange={e => {
                                    setProvinceId(e.target.value);
                                    setDistrictId("");
                                    setWardId("");
                                }}
                                className={`border rounded-xl px-4 py-4 text-2xl appearance-auto ${errors.province ? "border border-[#F32637]" : ""}`}
                            >
                                <option value="">-- Chọn tỉnh / thành --</option>
                                {provinces.map(p => (
                                    <option key={p.Id} value={p.Id}>
                                        {p.Name}
                                    </option>
                                ))}
                            </select>
                            {errors.province && (
                                <p className="text-[#F32637] text-base">
                                    {errors.province[0]}
                                </p>
                            )}
                        </div>

                        {/* District */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[1.4rem]">
                                Quận / Huyện<span className="text-red-500 ml-1">*</span>
                            </label>
                            <select
                                name="district_id"
                                value={districtId}
                                onChange={e => {
                                    setDistrictId(e.target.value);
                                    setWardId("");
                                }}
                                disabled={!provinceId}
                                className={`border rounded-xl px-4 py-4 text-2xl appearance-auto disabled:bg-gray-100 ${errors.district ? "border border-[#F32637]" : ""}`}
                            >
                                <option value="">-- Chọn quận / huyện --</option>
                                {districts.map(d => (
                                    <option key={d.Id} value={d.Id}>
                                        {d.Name}
                                    </option>
                                ))}
                            </select>
                            {errors.district && (
                                <p className="text-[#F32637] text-base">
                                    {errors.district[0]}
                                </p>
                            )}
                        </div>

                        {/* Ward */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[1.4rem]">
                                Phường / Xã<span className="text-red-500 ml-1">*</span>
                            </label>
                            <select
                                name="ward_id"
                                value={wardId}
                                onChange={e => setWardId(e.target.value)}
                                disabled={!districtId}
                                className={`border rounded-xl px-4 py-4 text-2xl appearance-auto disabled:bg-gray-100 ${errors.ward ? "border border-[#F32637]" : ""}`}
                            >
                                <option value="">-- Chọn phường / xã --</option>
                                {wards.map(w => (
                                    <option key={w.Id} value={w.Id}>
                                        {w.Name}
                                    </option>
                                ))}
                            </select>
                            {errors.ward && (
                                <p className="text-[#F32637] text-base">
                                    {errors.ward[0]}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-[1.4rem]">Ghi chú</label>
                        <input
                            name="note_address"
                            value={form.note_address}
                            onChange={handleChange}
                            placeholder="Chi tiết địa chỉ: hẻm, ngách, khu, số nhà, ...."
                            className={`border rounded-xl px-6 py-4 text-2xl ${errors.note_address ? "border border-[#F32637]" : ""}`}
                        />
                        {errors.note_address && (
                            <p className="text-[#F32637] text-base">
                                {errors.note_address[0]}
                            </p>
                        )}
                    </div>
                    <button type="submit" className="btn-dark font-semibold max-w-[30rem] m-auto">
                        Hoàn tất
                    </button>
                </form>
                }

            </div>

            <Footer />

            {loading && (
                <FormLoading loading={loading} />
            )}
        </>
    )
}

export default Profile